import { Helmet } from "react-helmet-async";

const DEFAULT_OG_IMAGE = "https://www.alvaru.com/og-image.png";

interface SeoHelmetProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  schema?: Record<string, any>;
  noindex?: boolean;
}

/**
 * SeoHelmet — Reusable SEO meta tag component.
 * Automatically appends " | Alvaru" to every title.
 * Renders: <title>, <meta description>, canonical, Open Graph, and Twitter Card tags.
 */
const SeoHelmet = ({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  schema,
  noindex = false,
}: SeoHelmetProps) => {
  const fullTitle = `${title} | Alvaru`;

  return (
    <Helmet>
      {/* Primary */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow" />
      }
      {!noindex && canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      {!noindex && canonical && <meta property="og:url" content={canonical} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* JSON-LD Schema */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

export default SeoHelmet;
